import { Link } from "@tanstack/react-router";
import { accentClasses, hasPlacement, type UrgencyPlacement } from "@/lib/urgency";
import { useUrgencyCampaign } from "@/hooks/useUrgency";
import { UrgencyCountdown } from "./UrgencyCountdown";

/**
 * Site-wide urgency strip. Renders only when an admin campaign is live and
 * includes the given placement. The countdown runs to the campaign's real
 * end time, and RLS stops serving the campaign once that passes.
 */
export function UrgencyStrip({
  placement = "strip",
  className = "",
}: {
  placement?: UrgencyPlacement;
  className?: string;
}) {
  const campaign = useUrgencyCampaign();
  if (!campaign || !hasPlacement(campaign, placement)) return null;

  return (
    <div
      className={`${accentClasses(campaign.accent)} ${className}`}
      role="region"
      aria-label="Sale announcement"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-3 py-1.5 text-center md:px-8">
        <span className="text-[12px] font-semibold uppercase tracking-[0.14em] md:text-[13px]">
          {campaign.headline}
        </span>
        {campaign.subline ? (
          <span className="text-[11.5px] opacity-85 md:text-xs">{campaign.subline}</span>
        ) : null}
        {campaign.coupon_code ? (
          <span className="rounded-full border border-current/40 px-2 py-0.5 text-[11px] font-semibold tracking-[0.08em]">
            {campaign.coupon_code}
          </span>
        ) : null}
        <UrgencyCountdown
          endsAt={campaign.ends_at}
          prefix="Ends in"
          className="text-[11.5px] font-semibold md:text-xs"
        />
        <Link
          to="/shop"
          className="text-[11.5px] font-semibold underline underline-offset-4 md:text-xs"
        >
          Shop the sale
        </Link>
      </div>
    </div>
  );
}
